import React from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { clx } from "../../../utils/Helpers";
import { UIFieldShell, useUIField } from "../form";

const hasValue = (value) => String(value ?? "").length > 0;

const UIInput = ({
  label,
  optionalLabel,
  required,
  helperText,
  error,
  id,
  name,
  onChange,
  onValueChange,
  onBlur,
  value,
  normalizeValue,
  classNames,
  className,
  size,
  maxLength,
  maxLengthMessage,
  inputProps,
  ...props
}) => {
  const fieldId = id || name;
  const field = useUIField({
    name,
    value,
    error,
    helperText,
  });
  const valueLength = String(field.value ?? "").length;
  const lang = useSelector((state) => state.locale.lang);
  const formatCount = (count) =>
    count.toLocaleString(lang === "ar" ? "ar-EG" : "en-US");
  const counterText =
    maxLength != null ? (
      // Isolated numbers: with a bare slash between them they'd read as one left-to-right number.
      <span dir={lang === "ar" ? "rtl" : "ltr"}>
        <bdi>{formatCount(valueLength)}</bdi>
        <span>/</span>
        <bdi>{formatCount(maxLength)}</bdi>
      </span>
    ) : null;
  /** With a message the limit is soft: typing past it is allowed but flagged as an error. */
  const softLimit = maxLength != null && Boolean(maxLengthMessage);
  const showLimitMessage = softLimit && valueLength > maxLength;
  const counterAtLimit = softLimit
    ? showLimitMessage
    : maxLength != null && valueLength >= maxLength;
  const resolvedInputProps =
    maxLength != null && !softLimit
      ? { ...inputProps, maxLength }
      : inputProps;

  const handleChange = (event) => {
    const nextValue = normalizeValue
      ? normalizeValue(event.target.value, event)
      : event.target.value;

    if (nextValue !== event.target.value) {
      event.target.value = nextValue;
    }

    field.setValue(nextValue);
    onChange?.(event);
    onValueChange?.(nextValue, event);
  };

  const handleBlur = (event) => {
    field.setTouched(true);
    onBlur?.(event);
  };

  return (
    <UIFieldShell
      id={fieldId}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      helperText={showLimitMessage ? maxLengthMessage : field.helperText}
      error={field.showError || showLimitMessage}
      classNames={classNames}
      counterText={counterText}
      counterAtLimit={counterAtLimit}
    >
      <TextField
        id={fieldId}
        name={name}
        required={required}
        error={field.showError || showLimitMessage}
        helperText={null}
        value={field.value ?? ""}
        className={clx(
          "ui-input",
          hasValue(field.value) ? "ui-input--filled" : "",
          className,
        )}
        size={size}
        inputProps={resolvedInputProps}
        onChange={handleChange}
        onBlur={handleBlur}
        {...props}
      />
    </UIFieldShell>
  );
};

UIInput.propTypes = {
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.any,
  normalizeValue: PropTypes.func,
  classNames: PropTypes.object,
  className: PropTypes.string,
  size: PropTypes.oneOf(["medium", "large"]),
  /** Hard-caps input length and shows a live "n/maxLength" counter under the field. */
  maxLength: PropTypes.number,
  /** Makes `maxLength` a soft limit: past it the field, counter and this message turn to error; the caller must block submit. */
  maxLengthMessage: PropTypes.node,
  inputProps: PropTypes.object,
};

UIInput.defaultProps = {
  label: null,
  optionalLabel: null,
  required: false,
  helperText: null,
  error: undefined,
  id: undefined,
  name: undefined,
  onChange: undefined,
  onValueChange: undefined,
  onBlur: undefined,
  value: undefined,
  normalizeValue: undefined,
  classNames: undefined,
  className: undefined,
  size: "medium",
  maxLength: undefined,
  maxLengthMessage: undefined,
  inputProps: undefined,
};

export default UIInput;
